import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect, useCallback } from 'react';

export default function Footer() {
  const navigate = useNavigate();
  const [emailValue, setEmailValue] = useState('');
  const [showEmailError, setShowEmailError] = useState(false);
  const [generateInputValue, setGenerateInputValue] = useState('');
  const [showGenerateError, setShowGenerateError] = useState(false);
  const [currentSubtext, setCurrentSubtext] = useState(0);
  const generateInputRef = useRef(null);
  
  const subtextOptions = [
    "a beautiful room",
    "switching out my sofa",
    "a floor remodel",
    "inspo for a client"
  ];

  // Subtext rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSubtext(prev => (prev + 1) % subtextOptions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleAuthClick = useCallback(() => {
    const width = 500;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    const popup = window.open(
      '/auth',
      'authPopup',
      `width=${width},height=${height},left=${left},top=${top},resizable=no,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );
    
    if (popup) {
      popup.focus();
    }
  }, []);

  const handleGenerateClick = useCallback(() => {
    if (!generateInputValue.trim()) {
      setShowGenerateError(true);
      if (generateInputRef.current) {
        generateInputRef.current.focus();
      }
      return;
    }
    setShowGenerateError(false);
    handleAuthClick();
  }, [generateInputValue, handleAuthClick]);

  const handleNavClick = (path) => {
    if (window.opener) {
      window.opener.location.href = path;
      window.close();
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  const handleEmailSubmit = () => {
    if (!emailValue.trim() || !emailValue.includes('@')) {
      setShowEmailError(true);
      return;
    }
    setShowEmailError(false);
    console.log('Email submitted:', emailValue);
    setEmailValue('');
    alert('Thanks! We\'ll keep you updated.');
  };

  const primaryLinks = [
    { name: 'Dashboard', path: '/dashboard', disabled: true },
    { name: 'Home', path: '/', disabled: false },
    { name: 'Pricing', path: '/pricing', disabled: false },
    { name: 'Features', path: '/features', disabled: false },
    { name: 'Support', path: '/support', disabled: false },
  ];

  const secondaryLinks = [
    { name: 'Blog', path: '/blog' },
    { name: 'Careers', path: '/careers' },
    { name: 'Press', path: '/press' },
    { name: 'Partnerships', path: '/partnerships' },
    { name: 'Acknowledgements', path: '/acknowledgements' },
    { name: 'Terms of Service', path: '/terms' },
    { name: 'Privacy Policy', path: '/privacy' },
  ];

  return (
    <footer style={{
      backgroundColor: '#2e2e2e',
      width: '100%',
      padding: '40px 20px 20px 20px',
      boxSizing: 'border-box',
      color: 'white',
      fontFamily: 'avenir-lt-pro, sans-serif',
      position: 'relative',
      zIndex: 10,
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Three Column Layout */}
        <div className="footer-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '40px',
          marginBottom: '0',
          position: 'relative',
        }}>
          
          {/* Vertical Separator 1 - Between Col 1 and 2 */}
          <div className="vertical-separator" style={{
            position: 'absolute',
            left: '33.333%',
            top: '0',
            bottom: '0',
            width: '1px',
            backgroundColor: '#555',
            transform: 'translateX(-50%)',
            zIndex: 1,
          }} />

          {/* Vertical Separator 2 - Between Col 2 and 3 */}
          <div className="vertical-separator" style={{
            position: 'absolute',
            left: '66.666%',
            top: '0',
            bottom: '0',
            width: '1px',
            backgroundColor: '#555',
            transform: 'translateX(-50%)',
            zIndex: 1,
          }} />
          
          {/* Column 1: Secondary Links - Thin font style */}
          <div className="footer-col-secondary" style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 2,
            paddingRight: '20px',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
            }}>
              {secondaryLinks.map((link, index) => (
                <div key={link.name}>
                  <div
                    onClick={() => handleNavClick(link.path)}
                    style={{
                      color: '#ccc',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                      padding: '3px 0',
                      lineHeight: '1.3',
                      fontFamily: 'avenir-lt-pro, sans-serif',
                      fontWeight: '400',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#40a3ff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#ccc'}
                  >
                    {link.name}
                  </div>
                  {/* Separator between items */}
                  {index < secondaryLinks.length - 1 && (
                    <div style={{
                      width: '100%',
                      height: '1px',
                      backgroundColor: '#555',
                      margin: '2px 0 5px 0',
                    }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Column 2: Primary Navigation - Button Style */}
          <div className="footer-col-primary" style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 2,
            paddingLeft: '20px',
            paddingRight: '20px',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}>
              {primaryLinks.map((link) => (
                <div
                  key={link.name}
                  onClick={() => !link.disabled && handleNavClick(link.path)}
                  style={{
                    color: link.disabled ? '#777' : 'white',
                    backgroundColor: link.disabled ? '#3d3d3d' : '#4d4d4d',
                    fontFamily: 'avenir-lt-pro, sans-serif',
                    fontWeight: '500',
                    borderRadius: '6px',
                    padding: '9px',
                    fontSize: '1rem',
                    textAlign: 'center',
                    cursor: link.disabled ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!link.disabled) {
                      e.currentTarget.style.backgroundColor = '#3a3a3a';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!link.disabled) {
                      e.currentTarget.style.backgroundColor = '#4d4d4d';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {link.name}
                </div>
              ))}
            </div>
          </div>

          {/* Column 3: Input Form + Copyright + Social */}
          <div className="footer-col-input" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 2,
            paddingLeft: '20px',
          }}>
            {/* Ready to Start Section - Centered */}
            <div className="ready-to-start-section" style={{
              width: '100%',
              maxWidth: '320px',
              margin: '0 auto',
            }}>
              <h4 style={{
                margin: '0 0 8px 0',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#aaa',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                textAlign: 'center',
              }}>
                Ready to Start?
              </h4>
              
              {/* Generate Input with Go Button */}
              <div style={{ 
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px',
              }}>
                <input
                  type="text"
                  placeholder={subtextOptions[currentSubtext]}
                  ref={generateInputRef}
                  value={generateInputValue}
                  onChange={(e) => {
                    setGenerateInputValue(e.target.value);
                    setShowGenerateError(false);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleGenerateClick();
                    }
                  }}
                  style={{
                    backgroundColor: '#3f3f3f',
                    color: 'white',
                    padding: '10px 12px',
                    border: showGenerateError ? '1px solid #ff6b6b' : 'none',
                    borderRadius: '8px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    textAlign: 'left',
                    outline: 'none',
                    flex: 1,
                    minWidth: '0',
                    boxSizing: 'border-box',
                    lineHeight: '1.2',
                  }}
                />
                
                <button 
                  onClick={handleGenerateClick}
                  style={{
                    backgroundColor: '#ffffffff',
                    color: 'black',
                    border: 'none',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    fontFamily: 'avenir-lt-pro, sans-serif',
                    fontWeight: '500',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  Go
                </button>
              </div>
              {showGenerateError && (
                <div style={{
                  color: '#ff6b6b',
                  fontSize: '0.7rem',
                  marginBottom: '0',
                  textAlign: 'center',
                }}>
                  This field is required
                </div>
              )}
            </div>

            {/* Horizontal Separator - Hidden on mobile, shown on desktop */}
            <div className="desktop-horizontal-separator" style={{
              width: '100%',
              maxWidth: '320px',
              height: '1px',
              backgroundColor: '#555',
              margin: '24px auto 16px auto',
            }} />

            {/* Copyright - Hidden on mobile, shown on desktop */}
            <div className="desktop-copyright-section" style={{
              width: '100%',
              maxWidth: '320px',
              margin: '0 auto',
            }}>
              <div style={{
                color: '#aaa',
                fontSize: '0.8rem',
                marginBottom: '8px',
                textAlign: 'left',
              }}>
                © 2026 Outlne, All Rights Reserved
              </div>

              {/* Social Icons - Left aligned */}
              <div style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}>
                <a 
                  href="https://instagram.com/tryoutlne" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    transition: 'opacity 0.2s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://tiktok.com/@outlne" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    transition: 'opacity 0.2s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.527V7.057a4.831 4.831 0 0 1-1.003-.371z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Copyright Section - Only visible on mobile */}
        <div className="mobile-copyright-section" style={{
          display: 'none',
        }}>
          <div style={{
            width: '100%',
            height: '1px',
            backgroundColor: '#555',
            margin: '20px 0',
          }} />
          <div style={{
            color: '#aaa',
            fontSize: '0.8rem',
            marginBottom: '8px',
            textAlign: 'left',
          }}>
            © 2026 Outlne, All Rights Reserved
          </div>
          <div style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}>
            <a 
              href="https://instagram.com/tryoutlne" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href="https://tiktok.com/@outlne" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.527V7.057a4.831 4.831 0 0 1-1.003-.371z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Responsive Styles */}
      <style>
        {`
          @media (max-width: 768px) {
            .footer-grid {
              display: flex !important;
              flex-direction: column !important;
              gap: 20px !important;
            }
            
            .vertical-separator {
              display: none !important;
            }
            
            /* Reorder for mobile */
            .footer-col-input {
              order: 1 !important;
              padding-left: 0 !important;
            }
            
            .footer-col-primary {
              order: 2 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            
            .footer-col-secondary {
              order: 3 !important;
              padding-right: 0 !important;
            }
            
            /* Hide desktop copyright section on mobile */
            .desktop-horizontal-separator,
            .desktop-copyright-section {
              display: none !important;
            }
            
            /* Show mobile copyright section */
            .mobile-copyright-section {
              display: block !important;
              order: 4 !important;
            }
            
            /* Add horizontal separator between input and primary links */
            .footer-col-input::after {
              content: '';
              display: block;
              width: 100%;
              height: 1px;
              background-color: #555;
              margin-top: 30px;
            }
            
            /* Center the ready to start section on mobile */
            .ready-to-start-section {
              max-width: 100% !important;
            }
          }
        `}
      </style>
    </footer>
  );
}